'use client';

import { type RefObject, useEffect } from 'react';

import { useThreadViewportStore } from '@assistant-ui/react';

/**
 * Keeps the conversation the reader is looking at still when this element changes height while it
 * sits entirely above the visible part of the thread, as the activity trail does when it folds at the
 * end of a reply the reader scrolled down to follow.
 *
 * Scroll anchoring is meant to do this, but Safari anchors only to changes outside the reply (and
 * Safari before 27 has no anchoring), and Chrome skips the trail's animated fold. When the browser
 * did keep the content in place, the element's bottom hasn't moved and there is nothing to correct.
 */
export function useKeepContentBelowInPlace({
  ref,
  active,
}: {
  ref: RefObject<HTMLElement | null>;
  /** Whether the element can still change height on its own; it is observed only meanwhile. */
  active: boolean;
}): void {
  const viewportStore = useThreadViewportStore({ optional: true });

  useEffect(() => {
    const element = ref.current;
    const scroller = viewportStore?.getState().element.viewport;
    if (!active || !element || !scroller) return;
    const viewport: HTMLElement = scroller;

    const bottomOf = () => element.getBoundingClientRect().bottom;
    // Where the element's bottom sat when the reader last scrolled. Every correction aims back at
    // this one point: browsers round scrollTop, so measuring afresh after each correction would let
    // the rounding add up over the frames of an animated fold.
    let heldBottom = bottomOf();
    let lastWidth = element.getBoundingClientRect().width;
    let correctedScrollTop: number | null = null;

    function onScroll() {
      // The scroll event of our own correction is not the reader moving.
      if (correctedScrollTop !== null && Math.abs(viewport.scrollTop - correctedScrollTop) < 1) {
        return;
      }
      correctedScrollTop = null;
      heldBottom = bottomOf();
    }

    // Runs after layout and before paint, so the corrected position is the first one drawn.
    const observer = new ResizeObserver(function holdContentBelow() {
      const { width, bottom } = element.getBoundingClientRect();
      const widthChanged = Math.abs(width - lastWidth) >= 0.5;
      lastWidth = width;
      // A width change (a rotated phone, a resized window) rewraps the whole conversation, which
      // the browser's own anchoring handles; only this element's own height change is ours to hold.
      if (widthChanged || heldBottom > viewport.getBoundingClientRect().top) {
        heldBottom = bottom;
        return;
      }
      // The held point stays put below half a pixel, so a fold's last sub-pixel frames add up to a
      // correction instead of each being taken as the new position.
      const drift = bottom - heldBottom;
      if (Math.abs(drift) < 0.5) return;
      viewport.scrollTop += drift;
      correctedScrollTop = viewport.scrollTop;
    });
    observer.observe(element);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [ref, active, viewportStore]);
}
